"use client";

import { Mail, Phone } from "lucide-react";
import useUserStore from "../stores/user.store.ts";
import { MaterialTypeEnum } from "@ns/types";
import { useNavigate, useLocation } from "react-router-dom";

export const Footer = () => {
  const { materialFilter, setMaterialFilter } = useUserStore();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user is on a single page (any path that's not the root)
  const isOnSinglePage = location.pathname !== "/";

  const handleMaterialFilter = (filter: MaterialTypeEnum | null) => {
    setMaterialFilter(filter);

    // If on a single page, redirect to main page
    if (isOnSinglePage) {
      navigate("/");
    }
  };

  return (
    <footer className="p-4 w-full border-t border-[#d9d9d9] bg-white mt-4">
      {/* Mobile Footer */}
      <div className="md:hidden">
        <div className="flex justify-center mb-4">
          <img
            src="/assets/radic.webp?height=40&width=40"
            alt="Mermeri Radic Logo"
            width={40}
            height={40}
            className="h-10 w-auto cursor-pointer"
            onClick={() => {
              setMaterialFilter(null);
              if (isOnSinglePage) navigate("/");
            }}
          />
        </div>
        <div className="flex flex-wrap justify-center gap-2 text-xs mb-4 max-w-full">
          <button
            className={`px-2 py-1 text-[#1E1E1E] rounded-full ${materialFilter === null ? "bg-[#f5f5f5] text-[#2c2c2c]" : "bg-white border border-[#d9d9d9] text-[#2c2c2c]"}`}
            onClick={() => handleMaterialFilter(null)}
          >
            Svi materijali
          </button>
          <button
            className={`px-2 py-1 text-[#1E1E1E] rounded-full ${materialFilter === MaterialTypeEnum.MARBLE ? "bg-[#f5f5f5] text-[#2c2c2c]" : "bg-white border border-[#d9d9d9] text-[#2c2c2c]"}`}
            onClick={() => handleMaterialFilter(MaterialTypeEnum.MARBLE)}
          >
            Mermer
          </button>
          <button
            className={`px-2 py-1 text-[#1E1E1E] rounded-full ${materialFilter === MaterialTypeEnum.QUARTZ ? "bg-[#f5f5f5] text-[#2c2c2c]" : "bg-white border border-[#d9d9d9] text-[#2c2c2c]"}`}
            onClick={() => handleMaterialFilter(MaterialTypeEnum.QUARTZ)}
          >
            Kvarc
          </button>
          <button
            className={`px-2 py-1 text-[#1E1E1E] rounded-full ${materialFilter === MaterialTypeEnum.TRAVERTINE ? "bg-[#f5f5f5] text-[#2c2c2c]" : "bg-white border border-[#d9d9d9] text-[#2c2c2c]"}`}
            onClick={() => handleMaterialFilter(MaterialTypeEnum.TRAVERTINE)}
          >
            Travertin
          </button>
          <button
            className={`px-2 py-1 text-[#1E1E1E] rounded-full ${materialFilter === MaterialTypeEnum.ONYX ? "bg-[#f5f5f5] text-[#2c2c2c]" : "bg-white border border-[#d9d9d9] text-[#2c2c2c]"}`}
            onClick={() => handleMaterialFilter(MaterialTypeEnum.ONYX)}
          >
            Onyx
          </button>
          <button
            className={`px-2 py-1 text-[#1E1E1E] rounded-full ${materialFilter === MaterialTypeEnum.GRANITE ? "bg-[#f5f5f5] text-[#2c2c2c]" : "bg-white border border-[#d9d9d9] text-[#2c2c2c]"}`}
            onClick={() => handleMaterialFilter(MaterialTypeEnum.GRANITE)}
          >
            Granit
          </button>
          <button
            className={`px-2 py-1 text-[#1E1E1E] rounded-full ${materialFilter === MaterialTypeEnum.QUARZITE ? "bg-[#f5f5f5] text-[#2c2c2c]" : "bg-white border border-[#d9d9d9] text-[#2c2c2c]"}`}
            onClick={() => handleMaterialFilter(MaterialTypeEnum.QUARZITE)}
          >
            Kvarcit
          </button>
        </div>
      </div>

      {/* Desktop Footer */}
      <div className="hidden md:flex justify-between items-center">
        <div className="space-y-1">
          <div
            onClick={() =>
              window.open(
                "https://www.mermeriradic.rs",
                "_blank",
                "noopener,noreferrer",
              )
            }
            className="bg-[#1e1e1e] cursor-pointer text-white px-3 py-1 rounded text-sm mb-1"
          >
            mermeriradic.rs
          </div>
          <a href="tel:+381653744438" className="block">
            <div className="bg-[#1e1e1e] text-white px-3 py-1 rounded text-sm mb-1 flex items-center cursor-pointer">
              <Phone className="w-4 h-4 mr-1" />
              +381 65 3744 438
            </div>
          </a>
          <a href="mailto:office@mermeriradic.rs" className="block">
            <div className="bg-[#1e1e1e] text-white px-3 py-1 rounded text-sm flex items-center cursor-pointer">
              <Mail className="w-4 h-4 mr-1" />
              office@mermeriradic.rs
            </div>
          </a>
        </div>

        <div className="flex items-center">
          <img
            src="/assets/radic.webp?height=60&width=60"
            alt="Mermeri Radic Logo"
            width={60}
            height={60}
            className="h-14 w-auto cursor-pointer"
            onClick={() =>
              window.open(
                "https://www.mermeriradic.rs",
                "_blank",
                "noopener,noreferrer",
              )
            }
          />
        </div>

        <div className="flex flex-col items-end space-y-1">
          <div className="flex space-x-2">
            <button
              className={`px-2 py-1 text-[#1E1E1E] text-xs ${materialFilter === null ? "font-semibold" : ""}`}
              onClick={() => handleMaterialFilter(null)}
            >
              Svi materijali
            </button>
            <button
              className={`px-2 py-1 text-[#1E1E1E] text-xs ${materialFilter === MaterialTypeEnum.MARBLE ? "font-semibold" : ""}`}
              onClick={() => handleMaterialFilter(MaterialTypeEnum.MARBLE)}
            >
              Mermer
            </button>
          </div>
          <div className="flex space-x-2">
            <button
              className={`px-2 py-1 text-[#1E1E1E] text-xs ${materialFilter === MaterialTypeEnum.QUARTZ ? "font-semibold" : ""}`}
              onClick={() => handleMaterialFilter(MaterialTypeEnum.QUARTZ)}
            >
              Kvarc
            </button>
            <button
              className={`px-2 py-1 text-[#1E1E1E] text-xs ${materialFilter === MaterialTypeEnum.TRAVERTINE ? "font-semibold" : ""}`}
              onClick={() => handleMaterialFilter(MaterialTypeEnum.TRAVERTINE)}
            >
              Travertin
            </button>
          </div>
          <div className="flex space-x-2">
            <button
              className={`px-2 py-1 text-[#1E1E1E] text-xs ${materialFilter === MaterialTypeEnum.ONYX ? "font-semibold" : ""}`}
              onClick={() => handleMaterialFilter(MaterialTypeEnum.ONYX)}
            >
              Onyx
            </button>
            <button
              className={`px-2 py-1 text-[#1E1E1E] text-xs ${materialFilter === MaterialTypeEnum.GRANITE ? "font-semibold" : ""}`}
              onClick={() => handleMaterialFilter(MaterialTypeEnum.GRANITE)}
            >
              Granit
            </button>
            <button
              className={`px-2 py-1 text-[#1E1E1E] text-xs ${materialFilter === MaterialTypeEnum.QUARZITE ? "font-semibold" : ""}`}
              onClick={() => handleMaterialFilter(MaterialTypeEnum.QUARZITE)}
            >
              Kvarcit
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};
